import { each, every, find, get, keyBy, keys, map, reduce, sortBy } from 'es-toolkit/compat';
import { groupBy } from 'es-toolkit/array';

import dayjs from '@skynet/formatting-date';
import { AssetType } from '@skynet/types-asset-type';
import type { JsonBalance } from 'src/store/shared/balances/types';
import type { AccountView } from '@skynet/data-shared-accounts';
import type {
  JsonBalanceHistoryAsset,
  JsonBalanceHistoryData,
  JsonBalanceHistoryExtended,
  JsonBalanceHistoryItem,
  SetInitialBalanceFormValues,
} from 'src/store/shared/balance-history/types';
import { historyItemTypeMap } from 'src/store/shared/balance-history/types';
import { ApplicationDomain } from 'src/routes/types';

import { getLineHue } from 'src/store/utils/color';

import { getGroupMap, getToken, isDla } from '../core/helpers';

import { buildComparisonData, getComparisonSnapshots } from '../calculations/comparison';
import type { ComparisonSnapshots } from '../calculations/comparison';
import { getHistoryWithTransactions, getTransactionsInRange } from '../transactions/processing';
import { getDoublingMap } from './helpers';
import { computeAccountHasError } from './getRowWarningInfo';
import type { GetExtendedBalancesHistoryTypes } from '../core/types';
import type { NaiveLocalDateTime } from 'src/store/shared/balance-history/helpers';

/** Parameters of {@link getExtendedBalancesHistoryBase}: everything but how transactions are applied. */
export type ExtendedBalancesHistoryBaseParams = Omit<
  GetExtendedBalancesHistoryTypes,
  'isUseTransitionsDiff'
>;

/** An asset of {@link ExtendedBalancesHistoryBase}: resolved snapshots instead of a comparison. */
export type ExtendedBalancesHistoryBaseAsset = Omit<JsonBalanceHistoryAsset, 'comparison'> & {
  comparisonSnapshots: ComparisonSnapshots;
  /** Deselected by the user, or an OTHER-type token while "ignore other tokens" is on. */
  isExcluded: boolean;
};

/** A row of {@link ExtendedBalancesHistoryBase}: its comparison-dependent fields are not set yet. */
export type ExtendedBalancesHistoryBaseRow = Omit<
  JsonBalanceHistoryExtended,
  'assets' | 'isComparisonSmallBalance'
> & {
  assets: ExtendedBalancesHistoryBaseAsset[];
};

/**
 * The part of the extended compare history that does not depend on how transactions are applied,
 * together with the settings needed to finish it with {@link withHistoryComparisons}.
 */
export interface ExtendedBalancesHistoryBase {
  rows: ExtendedBalancesHistoryBaseRow[];
  isInitialBalanceUsed: boolean;
  comparisonStartDate?: NaiveLocalDateTime;
  comparisonEndDate?: NaiveLocalDateTime;
  prices: NonNullable<GetExtendedBalancesHistoryTypes['prices']>;
  initialValues: NonNullable<GetExtendedBalancesHistoryTypes['initialValues']>;
  endDateSnapshot: string;
}

/**
 * Builds the compare history up to, but not including, each asset's comparison: accounts, synthetic
 * "current" snapshots, token grouping, transactions in range and the resolved start/end snapshots.
 *
 * This is the expensive part, and it is the same for the compare table and the compare list —
 * compute it once and finish each view with {@link withHistoryComparisons}.
 */
export const getExtendedBalancesHistoryBase = ({
  isInitialBalanceUsed = false,
  comparisonStartDate = undefined,
  comparisonEndDate = undefined,
  balances = [],
  history = {},
  coldWallets = [],
  coldWalletsSnapshots = {},
  exchanges = [],
  accounts = [],
  brokenAccounts = [],
  transactions = [],
  supportedTokens = [],
  tokenMap = {},
  showCurrentAccounts = false,
  prices = {},
  initialValues = {},
  endDateSnapshot = 'current',
  ignoreTokens = [],
  isIgnoreOtherTokens = false,
}: ExtendedBalancesHistoryBaseParams): ExtendedBalancesHistoryBase => {
  // A token is excluded from the view when the user deselected it, or when
  // "ignore other tokens" is on and it is an OTHER-type (type 2) token. The
  // hiding flags below are computed on the remaining (selected) assets so that
  // token selection drives row hiding exactly like the snapshot pipeline.
  const isExcludedAsset = (asset: Pick<JsonBalanceHistoryAsset, 'token' | 'type'>): boolean =>
    ignoreTokens.includes(asset.token) ||
    (isIgnoreOtherTokens && asset.type === historyItemTypeMap[2]);
  const coldWalletAccounts = coldWallets.map((wallet) => wallet.account);
  const allAccounts = [...accounts, ...brokenAccounts, ...coldWalletAccounts];

  const combinedBalances = [...balances, ...coldWallets];
  let combinedHistory = { ...history, ...coldWalletsSnapshots };

  if (
    !combinedBalances?.length ||
    !exchanges?.length ||
    !allAccounts?.length ||
    !comparisonStartDate ||
    !comparisonEndDate
  ) {
    return {
      rows: [],
      isInitialBalanceUsed,
      comparisonStartDate,
      comparisonEndDate,
      prices,
      initialValues,
      endDateSnapshot,
    };
  }

  // DLA > MM > OTHER (if DLA are shown)
  // MM > OTHER > DLA (if DLA are hidden)
  const sortBalances = sortBy(combinedBalances, (item) => !isDla(item.accountName));
  const filteredAccounts = allAccounts.filter(
    (account): account is AccountView => account !== undefined,
  );
  const newMap = getDoublingMap({ balances: sortBalances, accounts: filteredAccounts });

  // One lookup reused for every account (was an O(n) `find` per account, twice).
  const accountByName = keyBy(filteredAccounts, 'name');

  const balanceMap = groupBy(combinedBalances, (balance) => balance.accountID);

  const buildCurrentSnapshot = (balanceGroup: JsonBalance[], key: string) => {
    // Local "now", not dayjs.utc() — comparisonStartDate/comparisonEndDate are
    // naive "YYYY-MM-DD HH:mm" strings built from the user's local calendar day
    // (see useInitBalanceState.tsx / useComparisonDates.ts). Using the real UTC
    // instant here mislabels the snapshot onto the wrong calendar day whenever
    // the user's local date is ahead of UTC (SKY-4575).
    const dataKey: NaiveLocalDateTime = dayjs().format('YYYY-MM-DD HH:mm');
    return {
      [dataKey]: map(
        balanceGroup[0].assets,
        (item) =>
          ({
            accountID: key,
            date: dataKey,
            assetType: AssetType.Spot,
            entryPrice: 0,
            leverage: item.leverage,
            marginBalance: item.marginBalance,
            positionType: null,
            unrealizedPnL: 0,
            volume: item.volume,
            lastPrice: item.lastPrice,
            type: item.type,
            token: item.token,
            quotePrice: item.quotePrice ?? 1,
            isCurrent: true,
          }) satisfies JsonBalanceHistoryItem,
      ),
    };
  };

  each(balanceMap, (balanceGroup, key) => {
    const hasHistory = !!combinedHistory[key];

    if (!hasHistory) {
      // Same local-vs-UTC reasoning as buildCurrentSnapshot above (SKY-4575).
      const today = dayjs();
      const isInRange =
        today.isSameOrAfter(comparisonStartDate, 'day') &&
        today.isSameOrBefore(comparisonEndDate, 'day');

      const groupAssets = balanceGroup[0].assets;

      const acct = accountByName[key];
      const isErrorAccount = computeAccountHasError({
        status: acct?.status,
        message: acct?.message,
        hasAssets: (groupAssets?.length ?? 0) > 0,
      });

      // Inject a synthetic "current" snapshot for history-less accounts when
      // today is inside the comparison range — otherwise we'd misrepresent a
      // past period with today's balances. Error and empty accounts carry no
      // meaningful balance to misrepresent (they render a warning / "Assets are
      // missing"), so always include them so they stay visible in compare,
      // matching the snapshot table and balances list.
      if (groupAssets && (isInRange || groupAssets.length === 0 || isErrorAccount)) {
        combinedHistory[key] = buildCurrentSnapshot(balanceGroup, key);
      }
    } else if (showCurrentAccounts) {
      combinedHistory[key] = {
        ...combinedHistory[key],
        ...buildCurrentSnapshot(balanceGroup, key),
      };
    }
  });

  // Recalculate keys after potential synthetic history additions
  const historyMap = keys(combinedHistory);
  const supportTokenObj = keyBy(supportedTokens, 'token');

  const result = map(historyMap, (key, idx) => {
    const account = accountByName[key];
    let balance: JsonBalance | undefined = find(combinedBalances, { accountID: key });
    const exchange = find(exchanges, { id: balance?.exchangeID });
    const isWallet = balance?.isWallet ?? false;

    // Flatten history items from Record<date, items[]> to single array with dates
    const items: JsonBalanceHistoryItem[] = Object.entries(combinedHistory[key]).flatMap(
      ([date, historyItems]: [string, JsonBalanceHistoryData[]]) =>
        historyItems.map((item) => ({
          ...item,
          date,
          accountID: key,
        })),
    );

    // Group by token and type
    const tokensMap = groupBy(items, (item) => {
      const token = getToken(item.token, tokenMap);
      return `${token}__${item.type}`;
    });

    const assets = map(tokensMap, (tokenHistoryItems: JsonBalanceHistoryItem[], keyStr) => {
      const [token, type] = keyStr.split('__');
      const isDoubling = !isWallet ? newMap[key] : false;

      const transactionsInRange = getTransactionsInRange({
        transactions,
        accountId: key.toString(),
        token,
        startDate: comparisonStartDate,
        endDate: comparisonEndDate,
      });

      const comparisonSnapshots = getComparisonSnapshots(
        tokenHistoryItems,
        transactionsInRange,
        comparisonStartDate,
        comparisonEndDate,
        endDateSnapshot,
      );

      // `token` is the mapped label, while the account's balance is keyed by the raw symbol —
      // match through the token map so a mapped asset still resolves its price and leverage.
      const asset = find(balance?.assets, (item) => getToken(item.token, tokenMap) === token);
      const tokenTypeRaw = get(supportTokenObj, `${token}.type`);
      const tokenType =
        typeof tokenTypeRaw === 'string' || typeof tokenTypeRaw === 'number'
          ? String(tokenTypeRaw)
          : String(type ?? 'other');
      const assetType = historyItemTypeMap[tokenType];
      return {
        token,
        lastPrice: asset?.lastPrice ?? 0,
        leverage: asset?.leverage ?? 0,
        comparisonSnapshots,
        history: getHistoryWithTransactions(tokenHistoryItems, transactionsInRange) ?? [],
        isDoubling,
        type: assetType,
        isExcluded: isExcludedAsset({ token, type: assetType }),
      } satisfies ExtendedBalancesHistoryBaseAsset;
    });

    const isTotalDoubling = !isWallet ? newMap[key] : false;
    const baseItem = find(assets, { type: 'base' });
    const quoteItem = find(assets, { type: 'quote' });

    balance ??= {
      accountID: key,
      accountName: account?.label ?? '',
      isWallet,
      assets: [],
      exchangeID: exchange?.id ?? 0,
    };

    // Same shared rule as the snapshot pipeline, so error detection is
    // identical across every view (by status / message, regardless of assets).
    const hasError = computeAccountHasError({
      status: account?.status,
      message: account?.message,
      hasAssets: assets.length > 0,
    });

    // Apply the user's token selection before deciding whether the row is
    // empty / all-small, mirroring the snapshot view.
    const isAllTokensFiltered = assets.length > 0 && every(assets, (asset) => asset.isExcluded);

    return {
      ...balance,

      accountID: key.toString(),
      accountName: account?.label ?? '',
      isWallet,
      exchangeID: exchange?.id ?? 0,
      key: key.toString(),
      baseToken: baseItem ? getToken(baseItem.token, tokenMap) : undefined,
      quoteToken: quoteItem ? getToken(quoteItem.token, tokenMap) : undefined,
      assets,
      snapshotDates: keys(combinedHistory[key]),
      hasError,
      isTotalDoubling,
      isDla: isDla(account?.label ?? ''),
      isAllTokensFiltered,
      exchange,
      account,
      groupMap: getGroupMap({ exchange, account, isWallet }),
      piechartData: [],
      hue: getLineHue(idx),
    } satisfies ExtendedBalancesHistoryBaseRow;
  });

  return {
    rows: result,
    isInitialBalanceUsed,
    comparisonStartDate,
    comparisonEndDate,
    prices,
    initialValues,
    endDateSnapshot,
  };
};

/**
 * Finishes a compare history base (see {@link getExtendedBalancesHistoryBase}) by building every
 * asset's comparison.
 *
 * Returns the full extended history (with small / duplicate flags). Row hiding by the small-balances
 * and duplicates toggles is applied downstream by the selectors, so the compare token universe can
 * be derived from the complete set — matching how the snapshot view builds its token list from raw
 * data.
 *
 * @param base - The shared base.
 * @param isUseTransitionsDiff - `false` adds transactions to the start balance (compare table),
 * `true` subtracts them from the net change instead (compare list).
 */
export const withHistoryComparisons = (
  {
    rows,
    isInitialBalanceUsed,
    comparisonStartDate,
    comparisonEndDate,
    prices,
    initialValues,
    endDateSnapshot,
  }: ExtendedBalancesHistoryBase,
  isUseTransitionsDiff: boolean,
): JsonBalanceHistoryExtended[] =>
  rows.map(({ assets: baseAssets, ...row }) => {
    const assets = baseAssets.map(
      ({ comparisonSnapshots, isExcluded: _isExcluded, ...asset }) =>
        ({
          ...asset,
          comparison: buildComparisonData(
            comparisonSnapshots,
            isInitialBalanceUsed,
            comparisonStartDate,
            comparisonEndDate,
            isUseTransitionsDiff,
            prices,
            initialValues[row.accountID],
            endDateSnapshot,
          ),
        }) satisfies JsonBalanceHistoryAsset,
    );
    const visibleAssets = assets.filter((_, index) => !baseAssets[index].isExcluded);

    // Mirror the snapshot rule: an account is hideable by the small-balances
    // toggle when it is NOT an error account (status 0 / -2) and, after token
    // filtering, it has no visible assets (empty / all-filtered → collapses to
    // zeros) or all of its visible assets are small. Error accounts are governed
    // by the "show account errors" toggle instead.
    const acctStatusVal = row.account?.status?.valueOf();
    const isErrorStatus = acctStatusVal === 0 || acctStatusVal === -2;
    const isComparisonSmallBalance =
      !isErrorStatus &&
      (visibleAssets.length === 0 ||
        every(visibleAssets, (asset) => !asset.comparison || asset.comparison.isSmallBalance));

    return { ...row, assets, isComparisonSmallBalance } satisfies JsonBalanceHistoryExtended;
  });

/**
 * Builds the extended compare history for one view.
 *
 * When both the compare table and the compare list are needed, build the shared
 * {@link getExtendedBalancesHistoryBase} once and finish it per view with
 * {@link withHistoryComparisons} instead.
 */
export const getExtendedBalancesHistory = ({
  isUseTransitionsDiff = false,
  ...params
}: GetExtendedBalancesHistoryTypes): JsonBalanceHistoryExtended[] =>
  withHistoryComparisons(getExtendedBalancesHistoryBase(params), isUseTransitionsDiff);

/**
 * Creates initial form values from balance history assets for setting initial balances
 */
export const getCompareInitialValues = ({
  foundAsset,
  balance,
  isStart,
  domain,
  loanStartDate,
}: {
  foundAsset: JsonBalanceHistoryAsset[];
  balance: JsonBalanceHistoryExtended;
  isStart: boolean;
  domain: ApplicationDomain;
  loanStartDate: string;
}): Record<string, SetInitialBalanceFormValues> => {
  return reduce(
    foundAsset,
    (acc: Record<string, SetInitialBalanceFormValues>, val: JsonBalanceHistoryAsset) => {
      if (!val.comparison) return acc;

      const { startDate, endDate, startHistoryItem, endHistoryItem } = val.comparison;

      let date = isStart ? startDate : endDate;
      if (domain === ApplicationDomain.Loans && loanStartDate) {
        date = loanStartDate;
      }

      const historyItem = isStart ? startHistoryItem : endHistoryItem;
      const amount = historyItem?.volume ?? 0;

      return {
        ...acc,
        [val.token]: {
          accountID: balance.accountID,
          token: val.token,
          date,
          amount,
          type:
            val.type === 'base' || val.type === 'quote' || val.type === 'other'
              ? val.type
              : 'other',
        },
      };
    },
    {},
  );
};
